import React, { useEffect, useState } from "react"
import { BookOpen, Loader2, Play } from "lucide-react"
import {
  listPublishedTemplates,
  type StudentPublishedTemplate,
} from "../../../../api/studentPracticePapers"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import UseAiContentToggle from "./UseAiContentToggle"
import ContextModeSelect from "./ContextModeSelect"
import "./AdminTemplatesStep.style.scss"

const PAGE_SIZE = 5

interface TemplatesPagination {
  page: number
  total: number
  total_pages: number
}

interface AdminTemplatesStepProps {
  generatingTemplateId: string | null
  useAi: boolean
  contextMode: string
  onUseAiChange: (useAi: boolean) => void
  onContextModeChange: (mode: string) => void
  onUseTemplate: (template: StudentPublishedTemplate) => void
}

const AdminTemplatesStep = ({
  generatingTemplateId,
  useAi,
  contextMode,
  onUseAiChange,
  onContextModeChange,
  onUseTemplate,
}: AdminTemplatesStepProps) => {
  const [templates, setTemplates] = useState<StudentPublishedTemplate[]>([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<TemplatesPagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await listPublishedTemplates(page, PAGE_SIZE)
        if (cancelled) return
        if (res.data?.success && Array.isArray(res.data.data)) {
          setTemplates(res.data.data)
          const meta = res.data.pagination as
            | {
                page?: number
                total?: number
                total_pages?: number
                limit?: number
              }
            | undefined
          setPagination({
            page: meta?.page ?? page,
            total: meta?.total ?? res.data.data.length,
            total_pages: meta?.total_pages ?? 1,
          })
        } else {
          setTemplates([])
          setPagination(null)
          setError(res.data?.message || "Could not load templates")
        }
      } catch (err) {
        if (!cancelled) {
          setTemplates([])
          setPagination(null)
          setError(getLearnerApiErrorMessage(err, "Could not load templates"))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [page])

  const totalPages = Math.max(pagination?.total_pages ?? 1, 1)
  const showPager = Boolean(pagination && pagination.total > 0)

  return (
    <div className="admin-templates-step">
      <h2>
        <BookOpen size={20} />
        Predefined Templates (Admin)
      </h2>

      <UseAiContentToggle
        checked={useAi}
        disabled={Boolean(generatingTemplateId)}
        onChange={onUseAiChange}
      />
      <div className="admin-templates-step__context">
        <ContextModeSelect
          value={contextMode}
          disabled={Boolean(generatingTemplateId)}
          onChange={onContextModeChange}
        />
      </div>

      {loading ? (
        <p className="admin-templates-step__status">
          <Loader2 size={16} className="is-spinning" /> Loading templates…
        </p>
      ) : error ? (
        <p className="admin-templates-step__status is-error">{error}</p>
      ) : templates.length === 0 ? (
        <p className="admin-templates-step__status">
          No published templates yet. Ask your teacher to publish one.
        </p>
      ) : (
        <>
          <div className="admin-templates-step__list">
            {templates.map((template) => {
              const busy = generatingTemplateId === template.id
              const anyBusy = Boolean(generatingTemplateId)
              return (
                <div key={template.id} className="admin-templates-step__card">
                  <div>
                    <h3>{template.title}</h3>
                    <p>
                      {template.subjectTitle} · {template.count} Questions ·{" "}
                      {template.time}
                    </p>
                    {template.tags.length > 0 && (
                      <div className="admin-templates-step__tags">
                        {template.tags.map((tag) => (
                          <span key={tag}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={anyBusy || template.count <= 0}
                    title={
                      template.count <= 0
                        ? "Template has no question slots"
                        : undefined
                    }
                    onClick={() => onUseTemplate(template)}
                  >
                    {busy ? (
                      <>
                        <Loader2 size={16} className="is-spinning" />{" "}
                        Generating…
                      </>
                    ) : (
                      <>
                        <Play size={16} /> Use Template
                      </>
                    )}
                  </button>
                </div>
              )
            })}
          </div>

          {showPager && (
            <div className="admin-templates-step__pagination">
              <button
                type="button"
                disabled={page <= 1 || loading || Boolean(generatingTemplateId)}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
                {pagination && pagination.total > 0
                  ? ` · ${pagination.total} total`
                  : ""}
              </span>
              <button
                type="button"
                disabled={
                  page >= totalPages ||
                  totalPages <= 1 ||
                  loading ||
                  Boolean(generatingTemplateId)
                }
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AdminTemplatesStep
